import { DavetYenidenGonder } from "./DavetYenidenGonder";
import { useState } from "react";
import { TcKimlikDuzenle } from "./TcKimlikDuzenle";
import { DAVET_GUN } from "@koclukpanel/contracts";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Cip,
  HazirAvatar,
  hazirAvatarAdi,
  Ikon,
  Iskelet,
  Kart,
  FiltreSeridi,
  Rozet,
  Sekmeler,
  Tablo,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  avatarKaldir,
  avatarFotoBaglantisi,
  kisininAvatari,
  listeKesintiMetni,
  personel2faSifirla,
  personelArsivle,
  personelGuncelle,
  personelListesi,
  personelOlustur,
  personelRolleriDegistir,
  personelTcKimlikDegistir,
  rolleriGetir,
  sifirlamaIste,
  type PersonelSatiri,
  type RolOzeti,
} from "./api";
import { useSudoKapisi } from "./SudoKapisi";
import { KisiTakvimi } from "./Takvim";

/**
 * PERSONEL YÖNETİMİ — F-PPL
 *
 * ================================================================
 * BU EKRAN 06.08.2026'YA KADAR HİÇ YOKTU
 * ================================================================
 * `POST /personel`, `/personel/:id/roller`, `/:id/arsivle` uçlarının
 * hepsi sunucuda yazılmıştı ve görev listesinde madde `[x]`
 * işaretliydi — ama panelde ne ekran vardı ne de menüde girdi. Firma
 * sahibi kendi ekibini panelden ekleyemiyordu.
 *
 * ================================================================
 * ŞİFREYİ YÖNETİCİ BELİRLEMİYOR
 * ================================================================
 * F-PPL-01b. Form şifre alanı İÇERMİYOR ve bu bilinçli: davet
 * bağlantısı gidiyor, şifreyi kişi kendisi koyuyor. Yönetici hiçbir
 * aşamada personelin şifresini görmüyor — gördüğü an, o hesapla
 * yapılan işlemlerin kime ait olduğu tartışmalı hale gelirdi.
 *
 * ================================================================
 * ROL SEÇİMİ SUDO İSTER
 * ================================================================
 * Hem rol listesini OKUMAK (`sistem.rol.ata`) hem de personel açmak
 * sudo gerektiriyor. Ekran bunu kendi bilmiyor: sunucu
 * `SUDO_GEREKLI` dönünce `useSudoKapisi` şifreyi soruyor ve işlem
 * kaldığı yerden devam ediyor.
 */

const DURUM_ADI: Record<string, string> = {
  INVITED: "Davet gönderildi",
  ACTIVE: "Aktif",
  SUSPENDED: "Askıda",
  DISABLED: "Kapalı",
  LOCKED: "Kilitli",
};

const DURUM_TONU: Record<string, "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  INVITED: "bilgi",
  ACTIVE: "onay",
  SUSPENDED: "uyari",
  DISABLED: "notr",
  LOCKED: "hata",
};

type Dilim = "aktif" | "arsiv";

export function Personel({
  yonetebilir = false,
  benimId = null,
}: {
  yonetebilir?: boolean;
  /**
   * Giriş yapan kişinin kimliği.
   *
   * Sunucu zaten `assertNotSelf` ile kendi rolünü değiştirmeyi ve
   * kendi 2FA'sını sıfırlamayı reddediyor. Bunu ekranda da bilmek
   * gerekiyor: reddedilecek bir düğmeyi göstermek, kullanıcıya
   * yapamayacağı bir işi denettirmek olur.
   */
  benimId?: string | null;
}) {
  const [yeniAcik, setYeniAcik] = useState(false);
  const [acik, setAcik] = useState<string | null>(null);
  const [dilim, setDilim] = useState<Dilim>("aktif");
  const arsiv = dilim === "arsiv";

  const { sorguKapisi } = useSudoKapisi();

  /*
    ================================================================
    IKI DILIM DE SORULUYOR — CIZIM IKI SAYIYI DA GOSTERIYOR
    ================================================================
    Cizim sekmeleri "Aktif · 8" ve "Arşivlenmiş · 2" diye yaziyor.
    Yalnizca acik sekmeyi sorsaydik oteki sayiyi UYDURMAK ya da hic
    yazmamak gerekirdi; ilki yalan, ikincisi cizimden sapma.

    Ariza hâlinde sayi UYDURULMUYOR: `data` gelmemisse `toplam`
    `undefined` kaliyor ve `Sekmeler` o cipe sayi yazmiyor. Sifir
    yazmak "arsivde kimse yok" demek olurdu — oysa bilmiyoruz.
  */
  const sorgular = useQueries({
    queries: (["aktif", "arsiv"] as const).map((d) => ({
      queryKey: ["personel", d],
      queryFn: () => personelListesi(d === "arsiv"),
      retry: false,
    })),
  });
  const liste = arsiv ? sorgular[1]! : sorgular[0]!;

  if (yeniAcik) return <PersonelYeni onKapat={() => setYeniAcik(false)} />;

  if (liste.isPending) return <Iskelet satir={5} />;

  if (liste.isError) {
    const kapiEkrani = sorguKapisi(liste.error, () => void liste.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
    return (
      <BosDurum
        ton="hata"
        baslik="Personel listesi alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const kisiler = liste.data.personel;

  /*
    ================================================================
    `toplam` ARTIK GERCEK BIR TOPLAM — G-UYG-11 (04.09.2026)
    ================================================================
    Sabahki hâlinde uc `toplam: kullanicilar.length` donduruyordu, yani
    KIRPILMIS dizinin uzunlugunu. O yuzden buradaki "butun liste elde
    mi" kontrolu HIC DUSEMIYORDU ve kaldirilmisti; cumle de
    "N kişi listeleniyor" diyerek iddiayi kucultuyordu.

    Sunucu artik `{ toplam, gosterilen, sinir }` donduruyor. Kontrol
    GERI GELDI ve bu sefer gercekten dusebiliyor: `listeKesildi()`
    sunucudaki kuralin aynisini uyguluyor ve SINIRI sunucudan aliyor —
    sabiti kopyalamiyoruz, cunku kopyalanan sabit sunucu siniri
    degistigi gun sessizce ayrisirdi.
  */
  const kesinti = listeKesintiMetni(liste.data, "kişi");
  const rolSayisi = new Set(kisiler.flatMap((k) => k.roller)).size;

  /*
    ROL SAYISI YALNIZCA LISTE KESILMEDIYSE yaziliyor. Kesikken "7 rol"
    demek, ELDEKI 200 satirin rolunu butun kadronun rolu gibi
    gostermek olurdu — ve fark tam da rol cesitliliginin arttigi
    buyuk kadrolarda ortaya cikardi.
  */
  const kunye = `${liste.data.toplam} kişi` + (kesinti ? "" : ` · ${rolSayisi} rol`);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {/*
          Ekran BASLIGI burada YOK: kabuk onu `<h1>` olarak ciziyor
          (`Kabuk.tsx`). Cizimdeki "Personel · 4.1" basligindaki sayi
          sartname madde numarasi, arayuz metni degil.
        */}
        <p className="text-notr-600 text-sm">{kunye}</p>

        {yonetebilir && !arsiv && (
          <Buton onClick={() => setYeniAcik(true)}>
            <Ikon ad="ekle" boyut="kucuk" />
            Yeni personel
          </Buton>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Sekmeler
          etiket="Personel listesi dilimleri"
          secili={dilim}
          onSec={setDilim}
          sekmeler={[
            /*
              SAYI ARTIK `toplam`DAN — ve bu bir geri donus degil,
              DUZELTME. Sabah `personel.length` yazmistim cunku `toplam`
              kirpilmis dizinin uzunluguydu; simdi gercek sayim. Cip
              "Arşivlenmiş · 41" diyorsa arsivde 41 kisi VAR, ekranda
              10 tanesi gorunuyor olsa bile.
            */
            /*
              `?? undefined`: `toplam` artik `number | null` ve `null`
              "OLCULEMEDI" demek, sifir degil. Cip sayiyi hic
              gostermiyor — `0` yazmak, olculemeyeni "kimse yok" diye
              gostermek olurdu. `/personel` bugun null donmuyor ama
              sozlesme donebilecegini soyluyor ve ekran ona uymali.
            */
            { deger: "aktif", etiket: "Aktif", sayi: sorgular[0]?.data?.toplam ?? undefined },
            { deger: "arsiv", etiket: "Arşivlenmiş", sayi: sorgular[1]?.data?.toplam ?? undefined },
          ]}
        />
        {/*
          Cizimdeki bu cumle bir susleme degil: F-PPL-02 kalici silmeyi
          YASAKLIYOR ve "Arşivle" dugmesine basan yonetici ne olacagini
          basmadan once bilmeli.
        */}
        <p className="text-notr-600 text-xs sm:ml-auto">
          Arşivlenen kişi listeden silinmiyor, ayrı sekmede duruyor.
        </p>
      </div>

      {/*
        LISTE KESILDIYSE SOYLENIYOR — G-UYG-11.
        `FiltreSeridi` filtresiz de ciziliyor cunku bu cumlenin filtreyle
        ilgisi yok: kesilme filtresiz listede de olur. Kesilmediyse
        `listeKesintiMetni` `null` donuyor ve serit hic cizilmiyor.
      */}
      <FiltreSeridi filtreler={[]} onTemizle={() => {}} bilgi={kesinti} />

      {kisiler.length === 0 ? (
        <BosDurum
          baslik={arsiv ? "Arşivde kimse yok" : "Kapsamınızda personel yok"}
          aciklama={
            arsiv
              ? "Arşivlenen hesaplar burada durur; kalıcı silme yok." /* F-PPL-02 */
              : "Yalnızca kapsamınızdaki personel listelenir; bu, sistemde kayıt olmadığı anlamına gelmez."
          }
          eylem={
            yonetebilir && !arsiv ? (
              <Buton onClick={() => setYeniAcik(true)}>Yeni personel</Buton>
            ) : undefined
          }
        />
      ) : (
        <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart overflow-hidden border">
          <Tablo
            satirlar={kisiler}
            anahtar={(k) => k.id}
            eylemEtiketi="Ayrıntı"
            onSatir={(k) => setAcik(acik === k.id ? null : k.id)}
            altSatir={(k) =>
              acik === k.id ? (
                <div className="border-notr-100 bg-notr-50 flex flex-col gap-3 border-t p-4">
                  {/*
                    Takvim YONETIM iznine bagli DEGIL: kimin ne zaman
                    musait oldugunu gormek gunluk isin parcasi ve kapsam
                    kontrolunu sunucu yapiyor. Ayrinti gorup gormedigine
                    de sunucu karar veriyor (`mod: MESGULIYET`).
                  */}
                  {yonetebilir && k.archivedAt === null && k.davetBekliyor && k.id !== benimId && (
                    <DavetYenidenGonder hedef={{ kullaniciId: k.id }} izinli={yonetebilir} />
                  )}
                  {k.archivedAt === null && <KisiTakvimi userId={k.id} />}
                  {yonetebilir && k.archivedAt === null && <Islemler kisi={k} benimId={benimId} />}
                </div>
              ) : null
            }
            sutunlar={[
              {
                baslik: "Kişi",
                mobil: true,
                hucre: (k) => (
                  <span className="flex flex-col gap-px">
                    <span className="text-notr-900">
                      {k.firstName} {k.lastName}
                      {/*
                        CIZIM "kendiniz"i EYLEM SUTUNUNA yaziyor cunku
                        orada "Arşivle" dugmesi YOK. Bizde o sutunda
                        "Ayrıntı" var ve kendi satirini acmak SERBEST —
                        kapali olan yalnizca kendi rolunu degistirmek
                        (`assertNotSelf`). Isareti eylem sutununa koymak
                        "buraya bakamazsin" demek olurdu; adin yanina
                        koymak dogru olani soyluyor.
                      */}
                      {k.id === benimId && <span className="text-notr-600"> · siz</span>}
                    </span>
                    <span className="text-notr-600 text-xs">{k.email}</span>
                  </span>
                ),
              },
              {
                baslik: "Roller",
                mobil: true,
                hucre: (k) =>
                  k.roller.length === 0 ? (
                    <span className="text-notr-600">—</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {k.roller.map((r) => (
                        <Cip key={r}>{r}</Cip>
                      ))}
                    </span>
                  ),
              },
              {
                baslik: "Telefon",
                mobil: true,
                /*
                  BOS TELEFON BIR ARIZA DEGIL. Cizim bunu ayrica
                  yaziyor: "telefon alanı boş olan satır NORMAL, '—' ile
                  gösteriliyor". Bos hucre birakmak, verinin yuklenmedigi
                  izlenimini verirdi.
                */
                hucre: (k) =>
                  k.phone ? (
                    <span className="text-notr-800">{k.phone}</span>
                  ) : (
                    <span className="text-notr-600">—</span>
                  ),
              },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (k) => (
                  <Rozet ton={DURUM_TONU[k.status] ?? "notr"}>
                    {DURUM_ADI[k.status] ?? k.status}
                  </Rozet>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

function Islemler({ kisi, benimId }: { kisi: PersonelSatiri; benimId: string | null }) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState("");
  const { hataYakala, kapi } = useSudoKapisi(
    "Personel yetkilerini değiştirmek geri alınması zor sonuçlar doğurur.",
  );

  const arsivle = useMutation({
    mutationFn: () => personelArsivle(kisi.id, gerekce.trim()),
    onSuccess: async () => {
      setGerekce("");
      await qc.invalidateQueries({ queryKey: ["personel"] });
    },
    onError: hataYakala(() => arsivle.mutate()),
  });

  return (
    <div className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-3 border p-4">
      {kapi}

      {/*
        ================================================================
        KENDİ KAYDINDA YETKİ İŞLEMLERİ HİÇ GÖSTERİLMİYOR
        ================================================================
        Sunucu zaten reddediyor (`assertNotSelf`): kimse kendi rolünü
        değiştiremez, kendi 2FA'sını bu uçtan sıfırlayamaz. Düğmeyi
        göstermek, kullanıcıya reddedilecek bir işi denettirmek olurdu.

        Gösterilmemesi korumanın YERİNE geçmiyor — koruma sunucuda ve
        orada kalıyor; buradaki yalnızca gereksiz bir denemeyi
        engelliyor.
      */}
      <BilgiDuzenle kisi={kisi} />

      {/*
        T.C. AYRI EYLEM — bilgi formunun İÇİNDE DEĞİL.

        Sunucuda ayrı uç ve ayrı izin var (`kisi.tc_kimlik.degistir`).
        Alanı yukarıdaki forma koysaydık, izni olmayan biri formu
        doldurup kaydeder ve KAYDIN TAMAMI reddedilirdi — ad
        değişikliği de giderdi.
      */}
      <TcKimlikDuzenle
        mevcutMaskeli={kisi.nationalIdMaskeli}
        kaydet={(yeni) => personelTcKimlikDegistir(kisi.id, yeni)}
        onKaydedildi={() => void qc.invalidateQueries({ queryKey: ["personel"] })}
      />

      {benimId === kisi.id ? (
        <p className="rounded-alan border-notr-200 bg-notr-50 text-notr-700 border px-3 py-2 text-sm">
          Bu sizin hesabınız. Kendi rolünüzü değiştiremez, kendi iki adımlı doğrulamanızı buradan
          sıfırlayamazsınız — yedek kod ya da kurtarma zarfı kullanılır{/* F-TEC-20 */}.
        </p>
      ) : (
        <>
          <RolDegistir kisi={kisi} />
          <IkiAsamaSifirla kisi={kisi} />
        </>
      )}

      <SifreSifirlamaIste kisi={kisi} />

      <GorselKaldir kisi={kisi} />

      <p className="text-notr-600 text-sm">
        Roller: {kisi.roller.length > 0 ? kisi.roller.join(", ") : "—"}
      </p>

      {/*
        GEREKÇE ZORUNLU ve sunucu da zorunlu tutuyor (min 3 karakter).
        Bir hesabın neden kapatıldığı altı ay sonra sorulabilir olmalı
        ve denetim kaydına yazılıyor.
      */}
      <Alan
        etiket="Arşivleme gerekçesi"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        yardim="Denetim kaydına yazılır."
      />

      {arsivle.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {arsivle.error instanceof ApiHatasi ? arsivle.error.message : "İşlem tamamlanamadı."}
        </p>
      )}

      <div>
        <Buton
          gorunum="tehlike"
          yukleniyor={arsivle.isPending}
          disabled={gerekce.trim().length < 3}
          onClick={() => arsivle.mutate()}
        >
          Hesabı arşivle
        </Buton>
        {/*
          "Kalıcı sil" YOK — F-PPL-02. Arşivlenen kayıt raporlardan
          düşer, veri korunur. Düğmeyi koymamak, olmayan bir şeyi
          aramaktan iyi.
        */}
        <p className="text-notr-600 mt-1.5 text-xs">
          Kalıcı silme yok: hesap pasifleşir, oturumları kapanır, veri korunur.
        </p>
      </div>
    </div>
  );
}

/**
 * ROL DEĞİŞTİRME — F-ROL-15/16, G-SES-05
 *
 * Uç 06.08.2026'da yazıldı, bugüne kadar hiçbir ekran çağırmıyordu:
 * personel eklenirken rol veriliyordu ama sonradan DEĞİŞTİRMENİN yolu
 * yoktu. Yanlış rolle açılan bir hesabı düzeltmek için tek yol hesabı
 * arşivleyip yenisini açmaktı.
 *
 * Uç rolün TAM listesini alıyor (ekleme/çıkarma değil, değiştirme).
 * Ekran da öyle davranıyor: kutular mevcut durumla dolu başlar,
 * gönderilen şey son durumdur.
 */
function RolDegistir({ kisi }: { kisi: PersonelSatiri }) {
  const qc = useQueryClient();
  const [secili, setSecili] = useState<Set<string> | null>(null);
  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Rol değiştirmek kişinin görebildiği veriyi değiştirir; kimliğinizi doğrulayın.",
  );

  const roller = useQuery<RolOzeti[]>({
    queryKey: ["roller"],
    queryFn: rolleriGetir,
    retry: false,
  });

  const degistir = useMutation({
    mutationFn: (idler: string[]) => personelRolleriDegistir(kisi.id, idler),
    onSuccess: async () => {
      setSecili(null);
      await qc.invalidateQueries({ queryKey: ["personel"] });
      await qc.invalidateQueries({ queryKey: ["roller"] });
    },
    onError: hataYakala(() => degistir.mutate([...(secili ?? new Set<string>())])),
  });

  if (roller.isPending) return <Iskelet satir={3} />;

  if (roller.isError) {
    const kapiEkrani = sorguKapisi(roller.error, () => void roller.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
    return <p className="text-notr-600 text-sm">Rol listesi alınamadı; rol değiştirilemiyor.</p>;
  }

  const liste = roller.data;
  const mevcutIdler = new Set(liste.filter((r) => kisi.roller.includes(r.code)).map((r) => r.id));

  /*
    ================================================================
    GÖRÜNMEYEN ROL = KAYDETME YOK
    ================================================================
    Listede kişinin taşıdığı bir rol YOKSA (rol pasifleşmiş ya da
    benim göremediğim bir rol), kutulardan üretilen liste o rolü
    içermez. Uç TAM liste aldığı için kaydetmek o rolü SESSİZCE
    SİLERDİ — kullanıcı hiçbir şey değiştirmediğini sanırken.

    Bu yüzden kaydetme kapatılıyor: eksik bilgiyle yazmaktansa hiç
    yazmamak doğru.
  */
  const gorunmeyen = kisi.roller.filter((kod) => !liste.some((r) => r.code === kod));

  const isaretli = secili ?? mevcutIdler;
  const degisti =
    isaretli.size !== mevcutIdler.size || [...isaretli].some((id) => !mevcutIdler.has(id));

  const cevir = (id: string) => {
    const yeni = new Set(isaretli);
    if (yeni.has(id)) yeni.delete(id);
    else yeni.add(id);
    setSecili(yeni);
  };

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-3 border p-3">
      {kapi}
      <h4 className="text-notr-800 text-sm font-semibold">Roller</h4>

      {gorunmeyen.length > 0 && (
        <p role="alert" className="text-uyari-600 text-sm">
          Bu kişide sizin göremediğiniz {gorunmeyen.length} rol var ({gorunmeyen.join(", ")}).
          Kaydetmek onları silerdi; rol değişikliği kapatıldı.
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        {liste.map((r) => (
          <label key={r.id} className="text-notr-700 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={isaretli.has(r.id)}
              disabled={gorunmeyen.length > 0 || !r.isActive}
              onChange={() => cevir(r.id)}
            />
            <span>
              {r.name}
              {!r.isActive && <span className="text-notr-600 ml-1">(pasif)</span>}
              {r.description !== null && (
                <span className="text-notr-600 block text-xs">{r.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>

      {/*
        G-SES-05'i ÖNCEDEN söylüyoruz. Rol değişimi kişinin bütün
        oturumlarını kapatıyor; bunu sonradan öğrenmek "sistem beni
        attı" olarak yaşanır.
      */}
      <p className="text-notr-600 text-xs">
        Kaydedildiğinde bu kişinin açık bütün oturumları kapanır ve yeniden giriş yapması gerekir
        {/* G-SES-05 */}.
      </p>

      {degistir.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {degistir.error instanceof ApiHatasi ? degistir.error.message : "Roller değiştirilemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={degistir.isPending}
          disabled={!degisti || isaretli.size === 0 || gorunmeyen.length > 0}
          onClick={() => degistir.mutate([...isaretli])}
        >
          Rolleri kaydet
        </Buton>
        {degisti && (
          <Buton gorunum="ikincil" onClick={() => setSecili(null)}>
            Geri al
          </Buton>
        )}
      </div>
      {isaretli.size === 0 && (
        <p className="text-notr-600 text-xs">
          En az bir rol seçilmeli. Yetkiyi tamamen kaldırmak için hesabı arşivleyin.
        </p>
      )}
    </div>
  );
}

/**
 * 2FA SIFIRLAMA — G-AUT-03
 *
 * Telefonunu kaybeden personelin sisteme dönmesinin tek yolu. Sunucu
 * sudo istiyor, kayda geçiyor, hedefin oturumlarını kapatıyor ve
 * "sende olmayan yetkiye sahip birinin 2FA'sını sıfırlayamazsın"
 * kuralını uyguluyor. Ekranın işi yalnızca gerekçeyi toplamak.
 */
function IkiAsamaSifirla({ kisi }: { kisi: PersonelSatiri }) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState("");
  const [bitti, setBitti] = useState(false);
  const { hataYakala, kapi } = useSudoKapisi(
    "İki adımlı doğrulamayı sıfırlamak bir hesabın korumasını kaldırır.",
  );

  const sifirla = useMutation({
    mutationFn: () => personel2faSifirla(kisi.id, gerekce.trim()),
    onSuccess: async () => {
      setGerekce("");
      setBitti(true);
      await qc.invalidateQueries({ queryKey: ["personel"] });
    },
    onError: hataYakala(() => sifirla.mutate()),
  });

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-3 border p-3">
      {kapi}
      <h4 className="text-notr-800 text-sm font-semibold">İki adımlı doğrulama</h4>

      {bitti ? (
        <p className="text-notr-700 flex items-start gap-2 text-sm">
          <Ikon ad="tamam" className="text-onay-600 mt-0.5" />
          <span>
            Sıfırlandı. {kisi.firstName} bir sonraki girişinde uygulamasını yeniden kuracak; açık
            oturumları kapatıldı ve işlem denetim kaydına yazıldı.
          </span>
        </p>
      ) : (
        <>
          <p className="text-notr-600 text-xs">
            Telefonunu kaybeden personel için. Sıfırlandığında kişi bir sonraki girişinde uygulamayı
            yeniden kurar; oturumları kapanır ve işlem kritik olarak kayda geçer.
          </p>
          <Alan
            etiket="Sıfırlama gerekçesi"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            yardim="Denetim kaydına yazılır. En az 3 karakter."
          />
          {sifirla.isError && (
            <p role="alert" className="text-hata-700 text-sm font-medium">
              {sifirla.error instanceof ApiHatasi
                ? sifirla.error.message
                : "Sıfırlama tamamlanamadı."}
            </p>
          )}
          <div>
            <Buton
              gorunum="tehlike"
              yukleniyor={sifirla.isPending}
              disabled={gerekce.trim().length < 3}
              onClick={() => sifirla.mutate()}
            >
              İki adımlı doğrulamayı sıfırla
            </Buton>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * ŞİFRE SIFIRLAMA BAĞLANTISI GÖNDER
 *
 * Yönetici şifre BELİRLEMİYOR (F-PPL-01b) — sadece bağlantıyı
 * tetikliyor. Uç herkese açık ve adres kayıtlı olmasa da aynı cevabı
 * veriyor; o yüzden ekran kendi cümlesini uydurmuyor, sunucunun
 * cümlesini aynen gösteriyor.
 */
function SifreSifirlamaIste({ kisi }: { kisi: PersonelSatiri }) {
  const [mesaj, setMesaj] = useState<string | null>(null);

  const gonder = useMutation({
    mutationFn: () => sifirlamaIste(kisi.email),
    onSuccess: (c) => setMesaj(c.mesaj),
  });

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3">
      <h4 className="text-notr-800 text-sm font-semibold">Şifre</h4>
      <p className="text-notr-600 text-xs">
        Şifreyi siz belirlemezsiniz. <strong>{kisi.email}</strong> adresine sıfırlama bağlantısı
        gider, şifreyi kişi kendisi koyar.
      </p>
      {mesaj !== null && (
        <p className="text-notr-700 flex items-start gap-2 text-sm">
          <Ikon ad="tamam" className="text-onay-600 mt-0.5" />
          <span>{mesaj}</span>
        </p>
      )}
      {gonder.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {gonder.error instanceof ApiHatasi ? gonder.error.message : "İstek gönderilemedi."}
        </p>
      )}
      <div>
        <Buton gorunum="ikincil" yukleniyor={gonder.isPending} onClick={() => gonder.mutate()}>
          Şifre sıfırlama bağlantısı gönder
        </Buton>
      </div>
    </div>
  );
}

function PersonelYeni({ onKapat }: { onKapat: () => void }) {
  const qc = useQueryClient();
  const [ad, setAd] = useState("");
  const [soyad, setSoyad] = useState("");
  const [eposta, setEposta] = useState("");
  const [telefon, setTelefon] = useState("");
  /** G-AUT-00 — T.C. ile giriş için. İsteğe bağlı. */
  const [tcKimlik, setTcKimlik] = useState("");
  /** C39 — ortak numarada bu kişi mi telefonuyla giriyor. */
  const [telefonSahibi, setTelefonSahibi] = useState(false);
  const [secili, setSecili] = useState<Set<string>>(new Set());
  const [bitti, setBitti] = useState(false);
  const [yeniId, setYeniId] = useState("");
  const [davetGonderildi, setDavetGonderildi] = useState(false);

  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Personel açmak rol atamak demektir; kimliğinizi doğrulayın.",
  );

  const roller = useQuery<RolOzeti[]>({
    queryKey: ["roller"],
    queryFn: rolleriGetir,
    retry: false,
  });

  const olustur = useMutation({
    mutationFn: () =>
      personelOlustur({
        email: eposta.trim(),
        firstName: ad.trim(),
        lastName: soyad.trim(),
        phone: telefon.trim() || null,
        roleIds: [...secili],
        // Boş alan `null` gidiyor, `""` değil: `""` benzersizlik
        // kısıtına takılır ve T.C.'si girilmemiş ikinci personel
        // eklenemezdi.
        nationalId: tcKimlik.trim() || null,
        phoneIsPrimary: telefonSahibi,
      }),
    onSuccess: async (sonuc) => {
      setYeniId(sonuc.id);
      setDavetGonderildi(sonuc.davetGonderildi);
      setBitti(true);
      await qc.invalidateQueries({ queryKey: ["personel"] });
    },
    onError: hataYakala(() => olustur.mutate()),
  });

  if (bitti) {
    return (
      <Kart baslik="Personel eklendi">
        <div className="flex flex-col gap-3">
          {davetGonderildi ? (
            <p className="text-notr-700 flex items-start gap-2 text-sm">
              <Ikon ad="tamam" className="text-onay-600 mt-0.5" />
              <span>
                Davet e-postası <strong>{eposta.trim()}</strong> adresine gönderildi. Şifreyi kişi
                kendisi belirleyecek; bağlantı <strong>{DAVET_GUN} gün</strong> geçerli.
              </span>
            </p>
          ) : (
            <p role="alert" className="text-uyari-800 text-sm">
              Davet postası gönderilemedi — yeniden gönderin
            </p>
          )}
          {!davetGonderildi && (
            <DavetYenidenGonder
              hedef={{ kullaniciId: yeniId }}
              izinli={true}
              onGonderildi={() => setDavetGonderildi(true)}
            />
          )}
          <div className="flex flex-wrap gap-2">
            <Buton
              onClick={() => {
                setAd("");
                setSoyad("");
                setEposta("");
                setTelefon("");
                setSecili(new Set());
                setBitti(false);
              }}
            >
              Bir kişi daha ekle
            </Buton>
            <Buton gorunum="ikincil" onClick={onKapat}>
              Listeye dön
            </Buton>
          </div>
        </div>
      </Kart>
    );
  }

  if (roller.isPending) return <Iskelet satir={5} />;

  if (roller.isError) {
    // Rol listesi sudo istiyor; sifre alani BURADA cikiyor.
    const kapiEkrani = sorguKapisi(roller.error, () => void roller.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
    return (
      <BosDurum
        ton="hata"
        baslik="Roller alınamadı"
        aciklama="Rol seçilmeden personel eklenemez."
        eylem={
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        }
      />
    );
  }

  const eksik =
    ad.trim().length === 0 ||
    soyad.trim().length === 0 ||
    eposta.trim().length === 0 ||
    secili.size === 0;

  return (
    <Kart
      baslik="Yeni personel"
      aciklama="Şifreyi siz belirlemezsiniz — davet bağlantısı gider, kişi kendi şifresini koyar." /* F-PPL-01b */
    >
      <div className="flex flex-col gap-4">
        {kapi}

        <div className="grid gap-4 sm:grid-cols-2">
          <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />
          <Alan etiket="Soyad" value={soyad} onChange={(e) => setSoyad(e.target.value)} />
          <Alan
            etiket="E-posta"
            type="email"
            value={eposta}
            onChange={(e) => setEposta(e.target.value)}
            yardim="Davet buraya gidecek. Sonradan değiştirilemez."
          />
          <Alan
            etiket="Telefon"
            type="tel"
            value={telefon}
            onChange={(e) => setTelefon(e.target.value)}
            yardim="İsteğe bağlı."
          />
          <Alan
            etiket="T.C. kimlik numarası"
            value={tcKimlik}
            onChange={(e) => setTcKimlik(e.target.value)}
            inputMode="numeric"
            yardim="İsteğe bağlı. Girilirse bu numarayla da giriş yapabilir."
          />
        </div>

        {/*
          Kutu yalnızca telefon yazılınca görünüyor — telefonu olmayan
          birine "numaranın sahibi mi" diye sormak anlamsız ve
          işaretlenirse sunucu zaten reddediyor (C39).
        */}
        {telefon.trim().length > 0 && (
          <label className="text-notr-700 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={telefonSahibi}
              onChange={(e) => setTelefonSahibi(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Bu numaranın sahibi bu kişi
              <span className="text-notr-600 block text-xs">
                Aynı numarayı paylaşan başka bir kayıt varsa telefonla girişi sahibi yapar.
              </span>
            </span>
          </label>
        )}

        <div className="flex flex-col gap-2">
          <p className="text-notr-800 text-sm font-medium">Roller</p>
          {/*
            ROLSÜZ HESAP AÇILAMIYOR — sunucu da reddediyor
            ("Rolsüz hesap hiçbir şey yapamaz"). Kural burada da var ki
            kullanıcı gönderip hata almasın.

            ================================================================
            LİSTE SÜZÜLMÜYOR — VE BU BİR EKSİK, GİZLEMİYORUM
            ================================================================
            Sunucu sahip olmadığın izinleri içeren bir rolü reddediyor
            (F-ROL-15, `assertCanAssignRole`) ama bunu ancak GÖNDERDİKTEN
            sonra söylüyor. Canlı denemede görüldü: SUPER_ADMIN bile "PDR
            Uzmanı" rolünü atayamıyor, çünkü `calisma.hatadefteri.yaz` ve
            `gorusme.not_korumali.yaz` izinleri onda yok.

            Süzmek için sunucunun "bu rolü atayabilir misin" bilgisini
            `RolOzeti` ile göndermesi gerekiyor; `duzenleyebilirMiyim`
            alanı rolü DÜZENLEMEYİ anlatıyor, atamayı değil. O alan
            eklenene kadar liste tam gösteriliyor ve red mesajı hangi
            izinlerin eksik olduğunu AÇIKÇA yazıyor — sessizce
            "olmadı" demekten iyi.

            TODO(Faz 7.9): `RolOzeti.atayabilirMiyim` ekle, listeyi süz.
          */}
          <ul className="flex flex-col gap-1.5">
            {roller.data
              .filter((r) => r.isActive)
              .map((r) => (
                <li key={r.id}>
                  <label className="text-notr-700 flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={secili.has(r.id)}
                      onChange={(e) =>
                        setSecili((s) => {
                          const y = new Set(s);
                          if (e.target.checked) y.add(r.id);
                          else y.delete(r.id);
                          return y;
                        })
                      }
                      className="ring-notr-300 mt-0.5 size-4 rounded ring-1"
                    />
                    <span>
                      <span className="text-notr-900 font-medium">{r.name}</span>
                      {r.description && (
                        <span className="text-notr-600 block text-xs">{r.description}</span>
                      )}
                    </span>
                  </label>
                </li>
              ))}
          </ul>
          {secili.size === 0 && (
            <p className="text-notr-600 text-xs">
              En az bir rol seçin — rolsüz hesap hiçbir şey yapamaz.
            </p>
          )}
        </div>

        {olustur.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {olustur.error instanceof ApiHatasi ? olustur.error.message : "Personel eklenemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={olustur.isPending} disabled={eksik} onClick={() => olustur.mutate()}>
            Hesabı aç ve davet gönder
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * BİLGİ DÜZENLEME — F-PPL
 *
 * `PATCH /personel/:id` yazılıydı, hiçbir ekran çağırmıyordu: yanlış
 * yazılmış bir ad ya da değişen bir telefon numarası panelden
 * düzeltilemiyordu. Tek çare hesabı arşivleyip yenisini açmaktı.
 *
 * ================================================================
 * E-POSTA BU FORMDA YOK — SUNUCU DA KABUL ETMİYOR
 * ================================================================
 * Şema `email` almıyor. E-posta değişimi, bir hesabı ele geçirmenin
 * en kısa yolu: adresi kendi adresiyle değiştirip şifre sıfırlama
 * bağlantısı istemek yeter. Alanı ekranda göstermemek korumanın
 * yerine geçmiyor — koruma sunucuda ve orada kalıyor.
 *
 * Rol de burada yok: ayrı uç, sudo ve yükseltme koruması istiyor.
 */
function BilgiDuzenle({ kisi }: { kisi: PersonelSatiri }) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [ad, setAd] = useState(kisi.firstName);
  const [soyad, setSoyad] = useState(kisi.lastName);
  const [telefon, setTelefon] = useState(kisi.phone ?? "");

  const mut = useMutation({
    mutationFn: () => {
      // YALNIZCA DEĞİŞEN alanlar gönderiliyor. Hepsini göndermek
      // denetim kaydına "ad değişti" diye yazdırırdı — değişmediği
      // hâlde. Sunucu değişen alan ADLARINI kaydediyor.
      const veri: { firstName?: string; lastName?: string; phone?: string | null } = {};
      if (ad.trim() !== kisi.firstName) veri.firstName = ad.trim();
      if (soyad.trim() !== kisi.lastName) veri.lastName = soyad.trim();
      const yeniTel = telefon.trim() === "" ? null : telefon.trim();
      if (yeniTel !== kisi.phone) veri.phone = yeniTel;
      return personelGuncelle(kisi.id, veri);
    },
    onSuccess: async () => {
      setAcik(false);
      await qc.invalidateQueries({ queryKey: ["personel"] });
    },
  });

  const degisti =
    ad.trim() !== kisi.firstName ||
    soyad.trim() !== kisi.lastName ||
    (telefon.trim() === "" ? null : telefon.trim()) !== kisi.phone;

  const eksik = ad.trim().length === 0 || soyad.trim().length === 0;

  if (!acik) {
    return (
      <div>
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          Bilgileri düzenle
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-3 border p-3">
      <h4 className="text-notr-800 text-sm font-semibold">Bilgiler</h4>

      <div className="grid gap-3 sm:grid-cols-2">
        <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />
        <Alan etiket="Soyad" value={soyad} onChange={(e) => setSoyad(e.target.value)} />
        <Alan
          etiket="Telefon"
          value={telefon}
          onChange={(e) => setTelefon(e.target.value)}
          yardim="Boş bırakılabilir."
        />
      </div>

      <p className="text-notr-600 text-xs">
        E-posta ve rol buradan değişmez: e-posta değişimi bir hesabı ele geçirmenin en kısa yolu,
        rol değişimi ise ayrı bir yetki işlemi.
      </p>

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Bilgiler güncellenemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton yukleniyor={mut.isPending} disabled={!degisti || eksik} onClick={() => mut.mutate()}>
          Kaydet
        </Buton>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            setAd(kisi.firstName);
            setSoyad(kisi.lastName);
            setTelefon(kisi.phone ?? "");
            setAcik(false);
          }}
        >
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

/**
 * PROFİL GÖRSELİNİ KALDIRMA — F-PPL-13
 *
 * ================================================================
 * KALDIRMA VAR, DEĞİŞTİRME YOK
 * ================================================================
 * Şartname: *"başkasınınkini yönetici de değiştiremez, yalnızca
 * kaldırabilir (uygunsuz içerik)."* Katalogda "değiştir" diye bir
 * izin YOK — yani bu kural yanlışlıkla gevşetilemez.
 *
 * ================================================================
 * GÖRSEL ARTIK GÖRÜLEBİLİYOR — 07.09.2026
 * ================================================================
 * Bu bölüm yazıldığında `AvatarService` yalnızca `kendiGorselim` ve
 * `baskasininiKaldir` tanıyordu: yani **yönetici, kaldıracağı görseli
 * göremiyordu.** Uygunsuz içerik gerekçesiyle bir görseli kaldırmak,
 * önce ona bakmayı gerektirir.
 *
 * `GET /avatar/kisi/:userId` eklendi ve görsel kaldırma kutusunun
 * İÇİNDE gösteriliyor — listede DEĞİL. Listede göstermek otuz kişilik
 * bir sayfada otuz istek demekti; burada tek kişi ve yalnızca kutu
 * açıldığında.
 *
 * Görselin olmadığı da SÖYLENİYOR: kaldırılacak bir şey yoksa
 * yöneticinin gerekçe yazıp boşuna işlem yapmasının anlamı yok.
 */
/**
 * KALDIRILACAK GÖRSELİN ÖNİZLEMESİ — F-PPL-13
 *
 * Yönetici neye baktığını görmeden karar veremez. Yetkisi yoksa
 * sunucu 403 dönüyor ve kutu SESSİZ kalıyor: burada ayrı bir cümle
 * yazmak, kaldırma düğmesinin zaten söyleyeceği şeyi tekrar ederdi.
 */
function GorselOnizleme({ userId }: { userId: string }) {
  const gorsel = useQuery({
    queryKey: ["kisi-avatar", userId],
    queryFn: () => kisininAvatari(userId),
    retry: false,
  });

  const foto = useQuery({
    queryKey: ["kisi-avatar-foto", userId],
    queryFn: () => avatarFotoBaglantisi(userId),
    enabled: gorsel.data?.fotoVarMi === true,
    retry: false,
    // Bağlantı 15 dakika yaşıyor; 10 dakikada bayatlıyor ki ekranda
    // süresi dolmuş bir bağlantı kalmasın.
    staleTime: 10 * 60 * 1000,
  });

  if (gorsel.isPending) return <Iskelet satir={1} />;
  if (gorsel.isError) return null;

  const g = gorsel.data;

  if (!g.fotoVarMi && g.avatarPreset === null) {
    return (
      <p className="text-notr-600 text-sm">
        Bu kişinin yüklenmiş bir görseli yok; baş harfleri gösteriliyor. Kaldıracak bir şey yok.
      </p>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {g.fotoVarMi && foto.data ? (
        <img src={foto.data.url} alt="" className="size-16 rounded-full object-cover" />
      ) : g.avatarPreset !== null ? (
        <HazirAvatar secim={g.avatarPreset} boyut={64} />
      ) : (
        <div className="bg-marka-600 text-notr-0 flex size-16 items-center justify-center rounded-full text-xl font-semibold">
          {g.basHarfler ?? "—"}
        </div>
      )}
      <p className="text-notr-600 text-sm">
        {g.fotoVarMi
          ? "Yüklenmiş fotoğraf."
          : `${hazirAvatarAdi(g.avatarPreset ?? -1)} avatarı seçili — yüklenmiş fotoğraf yok.`}
      </p>
    </div>
  );
}

function GorselKaldir({ kisi }: { kisi: PersonelSatiri }) {
  const [acik, setAcik] = useState(false);
  const [gerekce, setGerekce] = useState("");
  const [bitti, setBitti] = useState(false);

  const mut = useMutation({
    mutationFn: () => avatarKaldir(kisi.id, gerekce.trim()),
    onSuccess: () => {
      setGerekce("");
      setBitti(true);
      setAcik(false);
    },
  });

  if (bitti) {
    return (
      <p className="rounded-alan border-notr-200 text-notr-700 flex items-start gap-2 border p-3 text-sm">
        <Ikon ad="tamam" className="text-onay-600 mt-0.5" />
        <span>
          Profil görseli kaldırıldı. {kisi.firstName} bundan sonra baş harflerini görecek ve isterse
          yeniden seçebilecek.
        </span>
      </p>
    );
  }

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Profil görselini kaldır
        </Buton>
      </div>
    );
  }

  return (
    <div className="rounded-alan border-notr-200 flex flex-col gap-3 border p-3">
      <h4 className="text-notr-800 text-sm font-semibold">Profil görselini kaldır</h4>
      <GorselOnizleme userId={kisi.id} />
      <p className="text-notr-600 text-xs">
        Uygunsuz içerik için. Görsel <strong>değiştirilmez, kaldırılır</strong>: kişi bundan sonra
        baş harflerini görür ve isterse yeniden seçer.
      </p>

      <Alan
        etiket="Kaldırma gerekçesi"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        yardim="Denetim kaydına yazılır. En az 3 karakter."
      />

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Görsel kaldırılamadı."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton
          gorunum="tehlike"
          yukleniyor={mut.isPending}
          disabled={gerekce.trim().length < 3}
          onClick={() => mut.mutate()}
        >
          Kaldır
        </Buton>
        <Buton gorunum="ikincil" onClick={() => setAcik(false)}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}
